function _rs(edge){ return edge && edge._private && edge._private.rscratch || {}; }
function _allpts(edge){
  const rs = _rs(edge);
  return Array.isArray(rs.allpts) ? rs.allpts : null;
}

function _interp(p, q, u){
  return { x: p.x + (q.x-p.x)*u, y: p.y + (q.y-p.y)*u };
}

function _tangent(p, q){
  const dx = q.x - p.x, dy = q.y - p.y;
  const L = Math.hypot(dx, dy) || 1;
  return { x: dx/L, y: dy/L };
}

function _normal(tan){
  return { x: -tan.y, y: tan.x };
}

/**
 * Which side of the link its label sits on, from the link's OVERALL direction
 * rather than the local tangent: on a segmented link the segment under the label
 * can point any way, so a per-segment decision flips the label whenever a bend
 * happens to run backwards.
 */
export function labelSideSign(first, last){
  if (!first || !last) return 1;
  const dx = last.x - first.x;
  if (dx < 0) return -1;
  if (dx > 0) return 1;
  return last.y < first.y ? -1 : 1;
}

function buildPathCacheFromRenderer(edge){
  const a = _allpts(edge);
  if(!a || a.length < 4) return null;

  if(a.length === 0) {
    try {
      edge.renderedBoundingBox();
      const a2 = _allpts(edge);
      if(!a2 || a2.length < 4) return null;
      return buildPathCacheFromPoints(a2);
    } catch(e) {
      return null;
    }
  }

  return buildPathCacheFromPoints(a);
}

function buildPathCacheFromPoints(a) {
  const pts = new Array(a.length / 2);
  for(let i = 0, k = 0; i < a.length; i += 2, k++){
    pts[k] = { x: a[i], y: a[i+1] };
  }

  const cum = new Array(pts.length);
  cum[0] = 0;
  let total = 0;
  for(let i = 1; i < pts.length; i++){
    const dx = pts[i].x - pts[i-1].x, dy = pts[i].y - pts[i-1].y;
    total += Math.hypot(dx, dy);
    cum[i] = total;
  }
  return { pts, cum, total };
}

const EdgePathSampler = (() => {
  const cache = new WeakMap();

  function ensure(edge){

    const c = cache.get(edge);
    const key = _allpts(edge);
    if(c && c._key === key && c.total > 0) return c;
    const built = buildPathCacheFromRenderer(edge);
    if(!built || built.total === 0){
      cache.delete(edge);
      return null;
    }
    built._key = key;
    cache.set(edge, built);
    return built;
  }

  function at(edge, t){
    const rs = _rs(edge);

    if(!rs || !rs.allpts || rs.allpts.length < 4){
      try{ edge.renderedBoundingBox(); }catch(e){}
      const rs2 = _rs(edge);
      if(!rs2 || !rs2.allpts || rs2.allpts.length < 4){
        return fallbackBezierRendered(edge, t);
      }
    }

    // 🔹 detect segmented edges by type
    if(edge.data('type') === 'STRAIGHT' && edge.data('bendPointPositions')?.length > 0){
      // Force edge to recalculate its internal cache by calling renderedBoundingBox
      try {
        edge.renderedBoundingBox();
      } catch(e) {
        // Ignore errors during bounding box calculation
      }

      const src = edge.source().position();
      const tgt = edge.target().position();

      // Force fresh calculation for segmented edges - don't rely on Cytoscape's internal cache
      // since it may not update properly when nodes move for custom segmented rendering
      return handleSegmentedEdge(src, tgt, edge.data('bendPointPositions'), t);
    }

    const pts = (rs && rs.allpts) ? rs.allpts : null;
    if(!pts || pts.length === 4 || rs.edgeType === 'haystack'){
      return fallbackBezierRendered(edge, t);
    }

    const samples = [];
    samples.push({ x: pts[0], y: pts[1] });

    for(let i = 2; i + 3 < pts.length; i += 4){
      const p0 = samples[samples.length - 1];
      const cp = { x: pts[i], y: pts[i + 1] };
      const p2 = { x: pts[i + 2], y: pts[i + 3] };
      const segmentSamples = 25;
      for(let j = 1; j <= segmentSamples; j++){
        const st = j / segmentSamples, u = 1 - st;
        samples.push({
          x: u*u*p0.x + 2*u*st*cp.x + st*st*p2.x,
          y: u*u*p0.y + 2*u*st*cp.y + st*st*p2.y
        });
      }
    }

    for(let jIdx = 25; jIdx < samples.length - 25; jIdx += 25){
      const smoothRadius = 3;
      for(let offset = -smoothRadius; offset <= smoothRadius; offset++){
        if(offset === 0) continue;
        const idx = jIdx + offset;
        if(idx > 0 && idx < samples.length - 1){
          const weight = 1 - Math.abs(offset) / (smoothRadius + 1);
          const prevPoint = samples[idx - 1];
          const nextPoint = samples[idx + 1];
          samples[idx] = {
            x: samples[idx].x * (1 - weight * 0.3) + (prevPoint.x + nextPoint.x) / 2 * weight * 0.3,
            y: samples[idx].y * (1 - weight * 0.3) + (prevPoint.y + nextPoint.y) / 2 * weight * 0.3
          };
        }
      }
    }

    const totalSamples = samples.length - 1;
    if(totalSamples <= 0){
      return fallbackBezierRendered(edge, t);
    }

    const targetIndex = t * totalSamples;
    const index = Math.floor(targetIndex);
    const localT = targetIndex - index;

    const idx0 = Math.min(index, samples.length - 1);
    const idx1 = Math.min(index + 1, samples.length - 1);

    const point = _interp(samples[idx0], samples[idx1], localT);

    const tangentWindow = 2;
    const tangentIdx0 = Math.max(0, idx0 - tangentWindow);
    const tangentIdx1 = Math.min(samples.length - 1, idx1 + tangentWindow);

    const tangent = _tangent(samples[tangentIdx0], samples[tangentIdx1]);
    const normal = _normal(tangent);

    return { point, tangent, normal, length: null, totalLength: null, segmentIndex: 0, u: t };
  }
  function atDistance(edge, d){
    const C = ensure(edge);
    if(!C || C.total === 0) return null;
    const target = Math.max(0, Math.min(d, C.total));

    let lo = 0, hi = C.cum.length - 1;
    while(lo < hi){
      const mid = (lo + hi) >> 1;
      if(C.cum[mid] < target) lo = mid + 1; else hi = mid;
    }
    const i = Math.max(1, lo);
    const segLen = C.cum[i] - C.cum[i-1] || 1;
    const u = (target - C.cum[i-1]) / segLen;

    const P = C.pts[i-1], Q = C.pts[i];
    const point = _interp(P, Q, u);

    let tan = _tangent(P, Q);
    if(!isFinite(tan.x) || !isFinite(tan.y)){
      const i0 = Math.max(0, i-2), i1 = Math.min(C.pts.length-1, i+1);
      tan = _tangent(C.pts[i0], C.pts[i1]);
    }
    const norm = _normal(tan);

    return { point, tangent: tan, normal: norm, length: target, totalLength: C.total, segmentIndex: i-1, u };
  }

  function atPercent(edge, t){
    return at(edge, t);
  }

  return { at, atPercent, atDistance, _rebuild: ensure };
})();

function fallbackBezierRendered(edge, t){
  const data = edge.data();
  const edgeType = data.type;

  const src = edge.source().position();
  const tgt = edge.target().position();
  if (!src || !tgt) return null;

  if (edgeType === 'STRAIGHT' && data.bendPointPositions && data.bendPointPositions.length > 0) {
    return handleSegmentedEdge(src, tgt, data.bendPointPositions, t);
  } else if (edgeType === 'UNBUNDLED_BEZIER' && data.controlPointPositions && data.controlPointPositions.length > 0) {
    return handleUnbundledBezier(edge, src, tgt, data.controlPointPositions, t);
  } else {
    const point = _interp(src, tgt, t);
    const tangent = _tangent(src, tgt);
    const normal = _normal(tangent);
    return { point, tangent, normal, length: null, totalLength: null, segmentIndex: 0, u: t };
  }
}

function handleUnbundledBezier(edge, src, tgt, controlPoints, t) {
  if (controlPoints.length === 2) {
    const cp1 = controlPoints[0];
    const cp2 = controlPoints[1];

    // Always use cubic Bezier for smooth curves without kinks
    // This treats the path as a single cubic curve: src -> cp1 -> cp2 -> tgt
    const u = 1 - t;

    // Cubic Bezier position
    const point = {
      x: u*u*u*src.x + 3*u*u*t*cp1.x + 3*u*t*t*cp2.x + t*t*t*tgt.x,
      y: u*u*u*src.y + 3*u*u*t*cp1.y + 3*u*t*t*cp2.y + t*t*t*tgt.y
    };

    // Cubic Bezier tangent (first derivative)
    const d = {
      x: 3*u*u*(cp1.x - src.x) + 6*u*t*(cp2.x - cp1.x) + 3*t*t*(tgt.x - cp2.x),
      y: 3*u*u*(cp1.y - src.y) + 6*u*t*(cp2.y - cp1.y) + 3*t*t*(tgt.y - cp2.y)
    };

    const L = Math.hypot(d.x, d.y) || 1;
    const tangent = { x: d.x/L, y: d.y/L };
    const normal = _normal(tangent);

    return { point, tangent, normal, length: null, totalLength: null, segmentIndex: 0, u: t };
  }

  // Fallback for other cases
  const point = _interp(src, tgt, t);
  const tangent = _tangent(src, tgt);
  const normal = _normal(tangent);
  return { point, tangent, normal, length: null, totalLength: null, segmentIndex: 0, u: t };
}

function handleSegmentedEdge(src, tgt, bendPoints, t) {
  // Create array of all points: src -> bendPoints -> tgt
  const allPoints = [src, ...bendPoints, tgt];

  // Calculate cumulative distances
  const distances = [0];
  let totalLength = 0;

  for (let i = 1; i < allPoints.length; i++) {
    const segLength = Math.hypot(
      allPoints[i].x - allPoints[i-1].x,
      allPoints[i].y - allPoints[i-1].y
    );
    totalLength += segLength;
    distances.push(totalLength);
  }

  if (totalLength === 0) {
    const point = _interp(src, tgt, t);
    const tangent = _tangent(src, tgt);
    const normal = _normal(tangent);
    return { point, tangent, normal, length: null, totalLength: null, segmentIndex: 0, u: t };
  }

  // Find target distance along path
  const targetDistance = t * totalLength;

  // Find which segment contains this distance
  let segmentIndex = 0;
  for (let i = 1; i < distances.length; i++) {
    if (distances[i] >= targetDistance) {
      segmentIndex = i - 1;
      break;
    }
  }

  // Interpolate within the segment
  const segStart = distances[segmentIndex];
  const segEnd = distances[segmentIndex + 1];
  const segLength = segEnd - segStart;
  const u = segLength > 0 ? (targetDistance - segStart) / segLength : 0;

  const p1 = allPoints[segmentIndex];
  const p2 = allPoints[segmentIndex + 1];

  const point = _interp(p1, p2, u);
  const tangent = _tangent(p1, p2);
  const normal = _normal(tangent);

  return { point, tangent, normal, length: targetDistance, totalLength, segmentIndex, u };
}

/**
 * Compute a point on a Cytoscape edge at a given ratio
 * @param {Object} edge - Cytoscape edge element
 * @param {number} t - Ratio along the edge (0-1)
 * @returns {Object|null} Point coordinates {x, y}
 */
function pointOnEdge(edge, t) {
  const result = EdgePathSampler.atPercent(edge, t);
  return result ? result.point : null;
}

/**
 * Get detailed information about a point on a Cytoscape edge
 * @param {Object} edge - Cytoscape edge element
 * @param {number} t - Ratio along the edge (0-1)
 * @returns {Object|null} Point details including position, tangent, normal
 */
function edgePointDetails(edge, t) {
  return EdgePathSampler.atPercent(edge, t);
}

/** `labelSideSign` for a whole edge, from its two endpoints. */
export function edgeLabelSideSign(edge){
  const first = edgePointDetails(edge, 0);
  const last = edgePointDetails(edge, 1);
  return labelSideSign(first && first.point, last && last.point);
}

export {
  pointOnEdge,
  edgePointDetails
};
